import { AnalyticsProvider } from '~/utils/analytics-provider';

/**
 * Facebook Pixel Analytics Provider
 * Professional implementation following provider interface
 */
export default class FacebookPixelProvider extends AnalyticsProvider {
  constructor() {
    super('Facebook Pixel');
    this.pixelLoaded = false;
  }

  /**
   * Initialize Facebook Pixel
   */
  initialize(config, propertyContext) {
    super.initialize(config, propertyContext);

    if (typeof window === 'undefined') {
      this.error('Cannot initialize Facebook Pixel in SSR environment');
      return;
    }

    this.loadPixelScript();
    this.initializePixel();
    this.setPropertyContext();
  }

  /**
   * Load Facebook Pixel script
   */
  loadPixelScript() {
    if (window.fbq) {
      this.pixelLoaded = true;
      return;
    }

    // Load Facebook Pixel
    !(function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js'));

    this.pixelLoaded = true;
    this.log('Script loaded successfully');
  }

  /**
   * Initialize pixel with ID
   */
  initializePixel() {
    if (!window.fbq || !this.config.pixel_id) {
      this.error('Pixel script not loaded or pixel_id missing');
      return;
    }

    window.fbq('init', this.config.pixel_id);
    window.fbq('track', 'PageView');

    this.log('Initialized with pixel ID:', this.config.pixel_id);
  }

  /**
   * Set property context for Facebook
   */
  setPropertyContext() {
    if (!window.fbq || !this.propertyContext) return;

    window.fbq('set', 'agent', 'plcustom', 'booking_engine');

    if (this.propertyContext) {
      window.fbq('set', 'customParameters', {
        property_name: this.propertyContext.property_name,
        property_brand: this.propertyContext.property_brand,
        property_location: `${this.propertyContext.location?.city}, ${this.propertyContext.location?.country}`
      });
    }
  }

  /**
   * Track events using Facebook Pixel
   */
  track(eventName, eventData) {
    if (!this.isReady() || !window.fbq) {
      this.error('Provider not ready or fbq not available');
      return;
    }

    const facebookEvent = this.mapEvent(eventName);
    const facebookData = this.formatEventData(eventName, eventData);

    try {
      if (facebookData) {
        window.fbq('track', facebookEvent, facebookData);
      } else {
        window.fbq('track', facebookEvent);
      }

      this.log(`Event tracked: ${facebookEvent}`, facebookData);
    } catch (error) {
      this.error('Failed to track event:', error);
    }
  }

  /**
   * Map standard events to Facebook events
   */
  mapEvent(standardEvent) {
    const facebookEventMap = {
      'page_view': 'PageView',
      'view_item': 'ViewContent',
      'add_to_cart': 'AddToCart',
      'remove_from_cart': 'RemoveFromCart',
      'begin_checkout': 'InitiateCheckout',
      'add_payment_info': 'AddPaymentInfo',
      'purchase': 'Purchase',
      'search': 'Search',
      'view_item_list': 'ViewContent'
    };

    return facebookEventMap[standardEvent] || 'CustomEvent';
  }

  /**
   * Format event data for Facebook Pixel
   */
  formatEventData(eventName, eventData) {
    const ecommerce = this.extractEcommerceData(eventData);

    switch (eventName) {
      case 'view_item':
      case 'add_to_cart':
      case 'remove_from_cart':
        return {
          content_type: 'accommodation',
          content_ids: ecommerce.items?.map(item => item.item_id) || [],
          content_name: this.propertyContext?.property_name,
          value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR'
        };

      case 'begin_checkout':
        return {
          content_type: 'accommodation',
          content_ids: ecommerce.items?.map(item => item.item_id) || [],
          value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR',
          num_items: ecommerce.items?.length || 0
        };

      case 'purchase':
        return {
          content_type: 'accommodation',
          content_ids: ecommerce.items?.map(item => item.item_id) || [],
          value: ecommerce.value || 0,
          currency: ecommerce.currency || 'EUR'
        };

      case 'search':
        return {
          search_string: eventData.search_term || '',
          content_type: 'accommodation'
        };

      case 'view_item_list':
        return {
          content_type: 'accommodation_list',
          content_category: 'hotel_rooms'
        };

      default:
        return null;
    }
  }

  /**
   * Get provider type for consent checking
   */
  getProviderType() {
    return 'marketing'; // Facebook Pixel is marketing/advertising
  }

  /**
   * Check if provider is ready
   */
  isReady() {
    return super.isReady() && this.pixelLoaded && window.fbq;
  }
}